import { computed } from 'vue';
import { useUISettings } from './useUISettings';
import { useAccount } from './useAccount';

const normalizeLocale = locale => locale?.replace(/-/g, '_');
const baseLocale = locale => normalizeLocale(locale)?.split('_')[0];

const localeCandidates = locale => {
  const normalizedLocale = normalizeLocale(locale);
  const normalizedBaseLocale = baseLocale(locale);

  return [normalizedLocale, normalizedBaseLocale].filter(Boolean);
};

/**
 * Select translation based on locale priority.
 * @param {Object} translations - Translations object with locale keys
 * @param {string} agentLocale - Agent's preferred locale
 * @param {string} accountLocale - Account's default locale
 * @returns {string|null} Selected translation or null
 */
export function selectTranslation(translations, agentLocale, accountLocale) {
  if (!translations || Object.keys(translations).length === 0) return null;

  for (const locale of localeCandidates(agentLocale)) {
    if (translations[locale]) {
      return translations[locale];
    }
  }

  for (const locale of localeCandidates(accountLocale)) {
    if (translations[locale]) {
      return translations[locale];
    }
  }

  return translations[Object.keys(translations)[0]];
}

/**
 * Resolve toggle label key for translated messages.
 * @param {Object} options
 * @param {string|null|undefined} options.translationDirection - incoming/outgoing
 * @param {boolean} options.showingMessageContent - whether current UI is showing message.content
 * @returns {string} i18n key
 */
export function getTranslationToggleLabelKey({
  translationDirection,
  showingMessageContent,
}) {
  if (translationDirection === 'outgoing') {
    return showingMessageContent
      ? 'CONVERSATION.VIEW_ORIGINAL'
      : 'CONVERSATION.VIEW_TRANSLATED';
  }

  return showingMessageContent
    ? 'CONVERSATION.VIEW_TRANSLATED'
    : 'CONVERSATION.VIEW_ORIGINAL';
}

/**
 * Composable to extract translation state/content from contentAttributes.
 * @param {Ref|Reactive} contentAttributes - Ref or reactive object containing `translations` property
 * @returns {Object} { hasTranslations, translationContent }
 */
export function useTranslations(contentAttributes) {
  const { uiSettings } = useUISettings();
  const { currentAccount } = useAccount();

  const hasTranslations = computed(() => {
    if (!contentAttributes.value) return false;
    const { translations = {} } = contentAttributes.value;
    return Object.keys(translations || {}).length > 0;
  });

  const translationContent = computed(() => {
    if (!hasTranslations.value) return null;
    return selectTranslation(
      contentAttributes.value.translations,
      uiSettings.value?.locale,
      currentAccount.value?.locale
    );
  });

  return { hasTranslations, translationContent };
}
